import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, UserCog, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { workspaceAdminAPI } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { CardSkeleton } from '../components/common/States';
import { EmptyState } from '../components/common/States';

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <Card>
    <CardHeader>
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div>
          <CardTitle className="text-hr-lg">{title}</CardTitle>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'hr_manager', label: 'HR Manager' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'workspace_admin', label: 'Workspace Admin' },
  { value: 'superadmin', label: 'Superadmin' },
];

//: Matches the floor the API enforces. Checked here too so the mistake is
//: caught before a round trip, never *instead* of the server checking it.
const MIN_PASSWORD_LENGTH = 8;

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** One user, on a page of its own.
 *
 *  Lifted out of the dialog in UsersCRUD because the identity fields and a
 *  password change do not fit a modal without one crowding the other — and a
 *  password change is worth its own deliberate section rather than a field
 *  someone fills in by accident while renaming somebody.
 */
const SuperadminUserEditPage = ({ userId, onBack, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    tenant_id: '',
    is_active: true,
  });
  const [passwordData, setPasswordData] = useState({ password: '', confirm: '' });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const [usersResponse, tenantsResponse] = await Promise.all([
          workspaceAdminAPI.listUsers(),
          workspaceAdminAPI.listTenants(),
        ]);
        if (!mounted) return;
        const rows = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
        const current = rows.find((row) => String(row.id) === String(userId));
        setTenants(Array.isArray(tenantsResponse?.data) ? tenantsResponse.data : []);
        if (!current) {
          setUser(null);
          return;
        }
        setUser(current);
        setFormData({
          email: current.email || '',
          first_name: current.first_name || '',
          last_name: current.last_name || '',
          role: current.role || 'employee',
          tenant_id: current.tenant_id ? String(current.tenant_id) : '',
          is_active: Boolean(current.is_active),
        });
      } catch (error) {
        if (mounted) toast.error(getErrorMessage(error, 'Failed to load user'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [userId]);

  // Reported as you type rather than on submit, so the mismatch is visible
  // next to the field that caused it.
  const passwordProblem = useMemo(() => {
    if (!passwordData.password && !passwordData.confirm) return null;
    if (passwordData.password.length < MIN_PASSWORD_LENGTH) {
      return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (passwordData.confirm && passwordData.password !== passwordData.confirm) {
      return 'The two passwords do not match.';
    }
    if (!passwordData.confirm) return 'Confirm the new password.';
    return null;
  }, [passwordData]);

  const changingPassword = Boolean(passwordData.password || passwordData.confirm);

  const handleSave = async () => {
    if (!user) return;
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (changingPassword && passwordProblem) {
      toast.error(passwordProblem);
      return;
    }

    try {
      setSaving(true);
      await workspaceAdminAPI.updateUser(user.id, {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        is_active: formData.is_active,
        tenant_id: formData.tenant_id ? Number(formData.tenant_id) : null,
        // Omitted entirely when untouched. Sending an empty string would read
        // as "set the password to nothing" to anything less careful than the
        // handler currently is.
        ...(changingPassword ? { password: passwordData.password } : {}),
      });
      setPasswordData({ password: '', confirm: '' });
      toast.success(
        changingPassword
          ? 'User updated. Their existing sessions have been signed out.'
          : 'User updated'
      );
      onSaved?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <CardSkeleton rows={3} />
        <CardSkeleton rows={4} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
        <EmptyState title="That user no longer exists." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Button>
        <Badge variant={user.is_active ? 'default' : 'secondary'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <SectionCard
        icon={UserCog}
        title="Identity &amp; Access"
        description={`Account details for ${user.email}.`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-first-name">First Name</Label>
            <Input
              id="user-first-name"
              value={formData.first_name}
              onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-last-name">Last Name</Label>
            <Input
              id="user-last-name"
              value={formData.last_name}
              onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-role">Role</Label>
            <select
              id="user-role"
              className={selectClass}
              value={formData.role}
              onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-status">Status</Label>
            <select
              id="user-status"
              className={selectClass}
              value={String(formData.is_active)}
              onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.value === 'true' }))}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="user-tenant">Organization</Label>
            <select
              id="user-tenant"
              className={selectClass}
              value={formData.tenant_id}
              onChange={(e) => setFormData((p) => ({ ...p, tenant_id: e.target.value }))}
            >
              <option value="">Select Organization</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={KeyRound}
        title="Password"
        description="Set a new password for this account. Leave both fields empty to keep the current one."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-password">New Password</Label>
            <div className="relative">
              <Input
                id="user-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                value={passwordData.password}
                onChange={(e) => setPasswordData((p) => ({ ...p, password: e.target.value }))}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-password-confirm">Confirm New Password</Label>
            <Input
              id="user-password-confirm"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={passwordData.confirm}
              onChange={(e) => setPasswordData((p) => ({ ...p, confirm: e.target.value }))}
            />
          </div>
        </div>

        {changingPassword && passwordProblem ? (
          <p className="text-sm text-destructive">{passwordProblem}</p>
        ) : null}
        {changingPassword && !passwordProblem ? (
          <p className="text-sm text-muted-foreground">
            Saving will sign this account out everywhere it is currently signed in.
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          The new password is not emailed to anyone — pass it on yourself, over something
          other than email if you can.
        </p>
      </SectionCard>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onBack} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || (changingPassword && Boolean(passwordProblem))}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default SuperadminUserEditPage;
